// WhyPortugalPR.js
import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Briefcase, GraduationCap, Shield, TrendingUp } from 'lucide-react';

const WhyPortugalPR = () => {
  const benefits = [
    {
      icon: Globe,
      title: 'A Door Into Europe',
      description: 'A Portuguese permit brings the Schengen Area, all 26 countries of it, within reach. Five years in you become eligible for citizenship and an EU passport, which opens 188 countries visa-free.'
    },
    {
      icon: Heart,
      title: 'The Day to Day',
      description: 'A Mediterranean climate, a long coastline, deep cultural roots, and living costs that stay within reason. Portugal is also counted year after year among the safest countries in Europe.'
    },
    {
      icon: Briefcase,
      title: 'Favourable Tax Treatment',
      description: 'The Non-Habitual Resident (NHR) programme carries tax advantages across a 10-year window. Around it sits a growing startup scene and policy that makes room for remote workers.'
    },
    {
      icon: GraduationCap,
      title: 'Healthcare and Schooling',
      description: 'Residents use the Portuguese public health and education systems, both to EU standards. International schools exist alongside them, and private healthcare stays affordable.'
    },
    {
      icon: Shield,
      title: 'Light Stay Requirements',
      description: 'The Golden Visa asks for only 7 days a year in the country, and the D7 keeps its own stay rules modest. On either one, a spouse and dependent children can join you.'
    },
    {
      icon: TrendingUp,
      title: 'Citizenship in the EU',
      description: 'Five years of residency makes you eligible to naturalise. Portugal permits dual citizenship, and a Portuguese passport carries the full set of EU rights.'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What Portuguese Residency Opens Up
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Investors, retirees and remote workers tend to arrive here for the same handful of reasons: entry into Europe, a gentler cost of living, and conditions that stay manageable.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg hover:shadow-lg transition-shadow"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center mb-4">
                  <Icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-primary mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            <strong>For context:</strong> more than 11,000 main applicants have been approved under the Golden Visa since 2012, which makes it one of the most heavily used residency-by-investment schemes anywhere in Europe. Where it leads, in the end, is EU citizenship and every right attached to it.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyPortugalPR;
